<template>
    <div>
        <Header />

        <!-- 麵包屑導覽 -->
        <nav aria-label="breadcrumb" class="breadcrumb_box">
            <div class="container">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/Profile" title="首頁">Home</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">
                        International Student Application System
                    </li>
                </ol>
            </div>
        </nav>

        <div class="main form">
            <a accesskey="c" class="accesskey" id="accesskey_c" href="javascript:void(0);" title="中央內容區塊"
                onclick="document.getElementById('accesskey_c').scrollIntoView();">
                :::
            </a>

            <div class="container">
                <div class="box">
                    <div class="title">
                        <h2>International Student Application System</h2>
                        <div class="btn_box" v-if="!isLoading">
                            <button class="btn" @click="printApplication" 
                                style="padding: 8px 20px; margin-bottom: -1px;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                                    stroke-width="2" style="vertical-align: middle; margin-right: 5px;">
                                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                                    <rect x="6" y="14" width="12" height="8"></rect>
                                </svg>
                                Print Application Form
                            </button>
                        </div>
                    </div>

                    <ul class="progress_bar">
                        <li v-for="(item, index) in progressItems" :key="index">
                            <router-link :to="item.to" :class="['step_box', getStepClass(index)]">
                                <div class="step">
                                    <p v-if="index != 6">Step {{ index + 1 }}</p>
                                    <p v-else>Payment</p>
                                    <p>{{ item.title }}</p>
                                </div>
                                <div class="line"></div>
                            </router-link>
                        </li>
                    </ul>

                    <!-- 載入指示器 -->
                    <div v-if="isLoading" class="loading-container" style="text-align: center; padding: 40px;">
                        <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                            <span class="sr-only">Loading...</span>
                        </div>
                        <p style="margin-top: 20px; font-size: 16px;">Loading application data...</p>
                    </div>

                    <template v-else>
                        <div class="bg">
                            <div class="title_box description">
                                <h3>Review & Submit Application</h3>
                            </div>

                            <div class="text_box">
                                <p>Please review all your application information carefully before submitting. Once
                                    submitted, you will not be able to modify your application (except payment proof in
                                    the payment section).</p>
                            </div>
                        </div>

                        <!-- Step 1: Personal Data Summary -->
                        <div class="bg" v-if="applicationData">
                            <div class="title_box">
                                <h3>Step 1: Personal Data</h3>
                                <!-- <button class="btn" @click="$router.push('/Apply/1')"
                                    style="font-size: 14px; padding: 5px 15px;">Edit</button> -->
                            </div>

                            <div class="text_box" style="width: 100%;">
                                <!-- Personal Information -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Personal Information</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <!-- Profile Picture -->
                                    <div style="font-weight: bold;">Profile Picture:</div>
                                    <div>
                                        <img v-if="applicationData.photoPreviewUrl"
                                            :src="$store.state.src + $store.state.subDirectory + applicationData.photoPreviewUrl"
                                            alt="Profile Photo"
                                            style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 2px solid #ddd;" />
                                        <span v-else style="color: #999;">Not uploaded</span>
                                    </div>

                                    <div style="font-weight: bold;">English Name:</div>
                                    <div>{{ applicationData.nameFirst }} {{ applicationData.nameMiddle }} {{
                                        applicationData.nameLast }}</div>

                                    <div style="font-weight: bold;">Chinese Name:</div>
                                    <div>{{ applicationData.chineseName || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Gender:</div>
                                    <div>{{ getGenderText(applicationData.gender) }}</div>

                                    <div style="font-weight: bold;">Date of Birth:</div>
                                    <div>{{ applicationData.dateOfBirth || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Marital Status:</div>
                                    <div>{{ getMaritalStatusText(applicationData.maritalStatus) }}</div>

                                    <div style="font-weight: bold;">Native Language:</div>
                                    <div>{{ applicationData.nativeLanguage || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Contact Email:</div>
                                    <div>{{ applicationData.contactEmail || 'N/A' }}</div>
                                </div>

                                <!-- Permanent Address -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Permanent Address</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Street Address:</div>
                                    <div>{{ applicationData.permStreet || 'N/A' }}</div>

                                    <div style="font-weight: bold;">City:</div>
                                    <div>{{ applicationData.permCity || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Country:</div>
                                    <div>{{ getCountryName(applicationData.permCountry) }}</div>

                                    <div style="font-weight: bold;">Zip Code:</div>
                                    <div>{{ applicationData.permZipCode || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Phone:</div>
                                    <div>{{ formatPhone(applicationData.permPhoneCountry, applicationData.permPhoneArea,
                                        applicationData.permPhoneNumber) }}</div>
                                </div>

                                <!-- Mailing Address -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Mailing Address</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Street Address:</div>
                                    <div>{{ applicationData.mailStreet || 'Same as Permanent Address' }}</div>

                                    <div style="font-weight: bold;">City:</div>
                                    <div>{{ applicationData.mailCity || 'Same as Permanent Address' }}</div>

                                    <div style="font-weight: bold;">Country:</div>
                                    <div>{{ applicationData.mailCountry ? getCountryName(applicationData.mailCountry) : 'Same as Permanent Address' }}</div>

                                    <div style="font-weight: bold;">Zip Code:</div>
                                    <div>{{ applicationData.mailZipCode || 'Same as Permanent Address' }}</div>

                                    <div style="font-weight: bold;">Phone:</div>
                                    <div>{{ formatPhone(applicationData.mailPhoneCountry, applicationData.mailPhoneArea,
                                        applicationData.mailPhoneNumber) }}</div>
                                </div>

                                <!-- Passport Information -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Passport Information</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Country of Birth:</div>
                                    <div>{{ getCountryName(applicationData.countryOfBirth) }}</div>

                                    <div style="font-weight: bold;">Country of Citizenship:</div>
                                    <div>{{ getCountryName(applicationData.countryOfCitizenship) }}</div>

                                    <div style="font-weight: bold;">Passport Number:</div>
                                    <div>{{ applicationData.passportNumber || 'N/A' }}</div>
                                </div>

                                <!-- Emergency Contact -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Emergency Contact</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Name:</div>
                                    <div>{{ applicationData.emergencyName || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Relationship:</div>
                                    <div>{{ applicationData.emergencyRelationship || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Email:</div>
                                    <div>{{ applicationData.emergencyEmail || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Phone:</div>
                                    <div>{{ formatPhone(applicationData.emergencyPhoneCountry,
                                        applicationData.emergencyPhoneArea, applicationData.emergencyPhoneNumber) }}
                                    </div>

                                    <div style="font-weight: bold;">Street Address:</div>
                                    <div>{{ applicationData.emergencyStreet || 'N/A' }}</div>

                                    <div style="font-weight: bold;">City:</div>
                                    <div>{{ applicationData.emergencyCity || 'N/A' }}</div>

                                    <div style="font-weight: bold;">Country:</div>
                                    <div>{{ getCountryName(applicationData.emergencyCountry) }}</div>

                                    <div style="font-weight: bold;">Zip Code:</div>
                                    <div>{{ applicationData.emergencyZipCode || 'N/A' }}</div>
                                </div>

                                <!-- Study Background -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Study Background</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Ever Studied in Taiwan:</div>
                                    <div>{{ getYesNoText(applicationData.studiedInTaiwan) }}</div>

                                    <div style="font-weight: bold;">Has Taiwan ARC:</div>
                                    <div>{{ getYesNoText(applicationData.hasTaiwanARC) }}</div>
                                </div>

                                <!-- Language Proficiency -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Language Proficiency</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">English Proficiency:</div>
                                    <div>{{ getLanguageProficiencyText(applicationData.languageEnglish) }}</div>

                                    <div style="font-weight: bold;">Mandarin Chinese Proficiency:</div>
                                    <div>{{ getLanguageProficiencyText(applicationData.languageMandarin) }}</div>
                                </div>

                                <!-- Financial Support -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Financial Support</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Source of Finance:</div>
                                    <div>{{ getFinanceSourcesText() }}</div>
                                </div>

                                <!-- ITRI & NTHU Program -->
                                <template v-if="applicationData.isITRIProgram == 1">
                                    <h4
                                        style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                        ITRI & NTHU Coordinated Graduate Program</h4>
                                    <div
                                        style="margin-bottom: 20px; padding: 15px; background: #f9f9f9; border-radius: 8px;">
                                        <div style="display: grid; grid-template-columns: 250px 1fr; gap: 12px;">
                                            <div style="font-weight: bold;">Application Status:</div>
                                            <div style="color: #4caf50; font-weight: bold;">Applied</div>

                                            <div style="font-weight: bold;">IIT Name:</div>
                                            <div>{{ applicationData.iitName || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Study Field in IIT:</div>
                                            <div>{{ applicationData.iitStudyField || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Graduation Date:</div>
                                            <div>{{ applicationData.iitGraduationDate || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Degree Received:</div>
                                            <div>{{ getITRIDegreeText(applicationData.iitDegreeReceived) }}</div>

                                            <div v-if="applicationData.iitDegreeReceived == 4"
                                                style="font-weight: bold;">Other Degree:</div>
                                            <div v-if="applicationData.iitDegreeReceived == 4">{{
                                                applicationData.iitDegreeOther || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Research Interests:</div>
                                            <div>{{ getITRIResearchInterests() }}</div>

                                            <div style="font-weight: bold;">Prospective Advisor:</div>
                                            <div>{{ applicationData.professorName || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Advisor Department:</div>
                                            <div>{{ applicationData.professorDepartment || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Advisor Email:</div>
                                            <div>{{ applicationData.professorEmail || 'N/A' }}</div>

                                            <div style="font-weight: bold;">Advisor Agrees to Collaborate:</div>
                                            <div>{{ applicationData.professorAgreeITRI == 1 ? 'Yes' : 'No' }}</div>
                                        </div>
                                    </div>
                                </template>

                                <!-- Family Data -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Family Data</h4>
                                <div style="margin-bottom: 20px;">
                                    <p style="font-weight: bold; margin-bottom: 10px;">Father's Information</p>
                                    <div
                                        style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; padding-left: 20px;">
                                        <div style="font-weight: bold;">Name:</div>
                                        <div>{{ applicationData.fatherName || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Country of Birth:</div>
                                        <div>{{ getCountryName(applicationData.fatherCountryOfBirth) }}</div>

                                        <div style="font-weight: bold;">Country of Citizenship:</div>
                                        <div>{{ getCountryName(applicationData.fatherCountryOfCitizenship) }}</div>

                                        <div style="font-weight: bold;">Phone:</div>
                                        <div>{{ formatPhone(applicationData.fatherPhoneCountry,
                                            applicationData.fatherPhoneArea,
                                            applicationData.fatherPhoneNumber) }}</div>
                                    </div>

                                    <p style="font-weight: bold; margin-bottom: 10px; margin-top: 20px;">Mother's
                                        Information</p>
                                    <div
                                        style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; padding-left: 20px;">
                                        <div style="font-weight: bold;">Name:</div>
                                        <div>{{ applicationData.motherName || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Country of Birth:</div>
                                        <div>{{ getCountryName(applicationData.motherCountryOfBirth) }}</div>

                                        <div style="font-weight: bold;">Country of Citizenship:</div>
                                        <div>{{ getCountryName(applicationData.motherCountryOfCitizenship) }}</div>

                                        <div style="font-weight: bold;">Phone:</div>
                                        <div>{{ formatPhone(applicationData.motherPhoneCountry,
                                            applicationData.motherPhoneArea,
                                            applicationData.motherPhoneNumber) }}</div>
                                    </div>
                                </div>

                                <!-- Academic Background -->
                                <h4 v-if="hasHighSchoolInfo() || hasUndergraduateInfo() || hasGraduateInfo()"
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Academic
                                    Background</h4>

                                <!-- High School -->
                                <template v-if="hasHighSchoolInfo()">
                                    <p style="font-weight: bold; margin-bottom: 10px;">High School</p>
                                    <div
                                        style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; padding-left: 20px; margin-bottom: 20px;">
                                        <div style="font-weight: bold;">Name of School:</div>
                                        <div>{{ applicationData.highSchoolName || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Street:</div>
                                        <div>{{ applicationData.highSchoolStreet || 'N/A' }}</div>

                                        <div style="font-weight: bold;">City:</div>
                                        <div>{{ applicationData.highSchoolCity || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Country:</div>
                                        <div>{{ getCountryName(applicationData.highSchoolCountry) }}</div>

                                        <div style="font-weight: bold;">Dates Attended:</div>
                                        <div>{{ applicationData.highSchoolAttended || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Date of Graduation:</div>
                                        <div>{{ applicationData.highSchoolGraduation || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Degree Received:</div>
                                        <div>{{ applicationData.highSchoolReceive || 'N/A' }}</div>
                                    </div>
                                </template>

                                <!-- Undergraduate -->
                                <template v-if="hasUndergraduateInfo()">
                                    <p style="font-weight: bold; margin-bottom: 10px;">Undergraduate</p>
                                    <div
                                        style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; padding-left: 20px; margin-bottom: 20px;">
                                        <div style="font-weight: bold;">Name of School:</div>
                                        <div>{{ applicationData.undergraduateName || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Street:</div>
                                        <div>{{ applicationData.undergraduateStreet || 'N/A' }}</div>

                                        <div style="font-weight: bold;">City:</div>
                                        <div>{{ applicationData.undergraduateCity || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Country:</div>
                                        <div>{{ getCountryName(applicationData.undergraduateCountry) }}</div>

                                        <div style="font-weight: bold;">Dates Attended:</div>
                                        <div>{{ applicationData.undergraduateAttended || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Date of Graduation:</div>
                                        <div>{{ applicationData.undergraduateGraduation || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Degree Received:</div>
                                        <div>{{ applicationData.undergraduateReceive || 'N/A' }}</div>
                                    </div>
                                </template>

                                <!-- Graduate -->
                                <template v-if="hasGraduateInfo()">
                                    <p style="font-weight: bold; margin-bottom: 10px;">Graduate</p>
                                    <div
                                        style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; padding-left: 20px; margin-bottom: 20px;">
                                        <div style="font-weight: bold;">Name of School:</div>
                                        <div>{{ applicationData.graduateName || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Street:</div>
                                        <div>{{ applicationData.graduateStreet || 'N/A' }}</div>

                                        <div style="font-weight: bold;">City:</div>
                                        <div>{{ applicationData.graduateCity || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Country:</div>
                                        <div>{{ getCountryName(applicationData.graduateCountry) }}</div>

                                        <div style="font-weight: bold;">Dates Attended:</div>
                                        <div>{{ applicationData.graduateAttended || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Date of Graduation:</div>
                                        <div>{{ applicationData.graduateGraduation || 'N/A' }}</div>

                                        <div style="font-weight: bold;">Degree Received:</div>
                                        <div>{{ applicationData.graduateReceive || 'N/A' }}</div>
                                    </div>
                                </template>

                                <!-- Other Information -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    Other
                                    Information</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0; margin-bottom: 20px;">
                                    <div style="font-weight: bold;">Has Taiwan ARC:</div>
                                    <div>
                                        {{ getYesNoText(applicationData.hasTaiwanARC) }}
                                        <span v-if="applicationData.hasTaiwanARC == 1"
                                            style="margin-left: 10px; color: #666;">
                                            (ARC No: {{ applicationData.taiwanARCNumber || 'N/A' }})
                                        </span>
                                    </div>

                                    <div style="font-weight: bold;">Has Taiwan APRC:</div>
                                    <div>
                                        {{ getYesNoText(applicationData.hasTaiwanAPRC) }}
                                        <span v-if="applicationData.hasTaiwanAPRC == 1"
                                            style="margin-left: 10px; color: #666;">
                                            (APRC No: {{ applicationData.taiwanAPRCNumber || 'N/A' }})
                                        </span>
                                    </div>
                                </div>

                                <!-- Information Source -->
                                <h4
                                    style="color: #580d63; margin-bottom: 15px; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                    How did you hear
                                    about us?</h4>
                                <div
                                    style="display: grid; grid-template-columns: 250px 1fr; gap: 12px; padding: 10px 0;">
                                    <div style="font-weight: bold;">Message Source:</div>
                                    <div>{{ getMessageSourcesText() }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Applied Programs -->
                        <div class="bg" v-if="userPrograms.length > 0">
                            <div class="title_box">
                                <h3>Step 2: Applied Programs</h3>
                                <!-- <button class="btn" @click="$router.push('/Apply/2')"
                                    style="font-size: 14px; padding: 5px 15px;">Edit</button> -->
                            </div>

                            <ul class="department_list">
                                <li v-for="(item, index) in userPrograms" :key="item.pId">
                                    <div>
                                        <div class="number">{{ index + 1 }}</div>
                                    </div>
                                    <div class="department">
                                        <p>{{ item.college }}</p>
                                        <p>{{ item.program }}</p>
                                        <p style="color: #888; font-size: 14px;">{{ item.degree }}</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <!-- Step 2: Scholarship Applications -->
                        <div class="bg" v-if="hasScholarshipApplications()">
                            <div class="title_box">
                                <h3>Scholarship Applications</h3>
                            </div>

                            <div class="text_box" style="width: 100%;">
                                <div v-for="scholarship in getAppliedScholarships()" :key="scholarship.sId"
                                    style="border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin-bottom: 20px;">

                                    <!-- 獎學金標題與說明 -->
                                    <div style="margin-bottom: 15px;">
                                        <h5
                                            style="margin: 0 0 10px 0; color: #580d63; font-weight: 600; display: flex; align-items: center; gap: 10px;">
                                            {{ scholarship.name }}
                                            <span v-if="scholarship.type === 'country'"
                                                style="display: inline-block; padding: 3px 10px; background: #2196F3; color: white; border-radius: 3px; font-size: 12px; font-weight: normal;">
                                                Country Scholarship
                                            </span>
                                            <span v-else-if="scholarship.type === 'program'"
                                                style="display: inline-block; padding: 3px 10px; background: #9C27B0; color: white; border-radius: 3px; font-size: 12px; font-weight: normal;">
                                                Program Scholarship
                                            </span>
                                        </h5>
                                        <div v-if="scholarship.content" v-html="scholarship.content"
                                            style="margin-bottom: 10px; color: #666; font-size: 14px; line-height: 1.6;">
                                        </div>
                                    </div>

                                    <!-- 申請狀態 -->
                                    <div style="margin-bottom: 15px;">
                                        <span
                                            style="display: inline-block; padding: 5px 15px; background: #4caf50; color: white; border-radius: 4px; font-size: 14px; font-weight: bold;">
                                            ✓ Applied
                                        </span>
                                    </div>

                                    <!-- 已上傳的文件 -->
                                    <div v-if="scholarship.fileUrl"
                                        style="margin-top: 15px; padding: 15px; background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 5px;">
                                        <p style="font-weight: bold; margin-bottom: 10px; color: #580d63;">
                                            Uploaded Application Document:
                                        </p>
                                        <a v-if="scholarship.fileUrl"
                                            :href="$store.state.src + $store.state.subDirectory + scholarship.fileUrl"
                                            target="_blank" class="file_a">
                                            Download
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 3: Recommenders -->
                        <div class="bg" v-if="recommenders.length > 0">
                            <div class="title_box">
                                <h3>Step 3: Recommenders</h3>
                                <!-- <button class="btn" @click="$router.push('/Apply/3')"
                                    style="font-size: 14px; padding: 5px 15px;">Edit</button> -->
                            </div>

                            <div class="text_box" style="width: 100%;">
                                <div v-for="(rec, index) in recommenders" :key="rec.arId"
                                    style="margin-bottom: 20px; padding: 15px; background: #f9f9f9; border-radius: 8px;">
                                    <h4 style="margin-bottom: 10px; color: #580d63;">Recommender {{ index + 1 }}</h4>
                                    <div style="display: grid; grid-template-columns: 150px 1fr; gap: 10px;">
                                        <div style="font-weight: bold;">Name:</div>
                                        <div>{{ rec.name }}</div>

                                        <div style="font-weight: bold;">Email:</div>
                                        <div>{{ rec.email }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Step 4 & 5: Uploaded Documents Status -->
                        <div class="bg">
                            <div class="title_box">
                                <h3>Step 4 & 5: Required Documents</h3>
                            </div>

                            <div class="text_box">
                                <div v-if="documentsStatus">
                                    <!-- General Documents (Step 4) -->
                                    <div style="margin-bottom: 30px;">
                                        <h4
                                            style="margin-bottom: 15px; color: #580d63; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                            General Documents (Step 4)</h4>

                                        <div v-if="documentsStatus.generalComplete"
                                            style="color: #4caf50; font-weight: bold; margin-bottom: 15px;">
                                            ✓ All required general documents uploaded
                                        </div>
                                        <div v-else style="color: #f44336; font-weight: bold; margin-bottom: 15px;">
                                            ✗ Missing required documents
                                            <!-- <button class="btn" @click="$router.push('/Apply/4')"
                                                style="font-size: 14px; padding: 5px 15px; margin-left: 10px;">Go to
                                                Step 4</button> -->
                                        </div>

                                        <!-- 顯示缺少的通用文件列表 -->
                                        <div
                                            v-if="!documentsStatus.generalComplete && documentsStatus.missingGeneralFiles && documentsStatus.missingGeneralFiles.length > 0">
                                            <p style="font-weight: bold; margin-top: 15px; margin-bottom: 10px;">Missing
                                                Files:</p>
                                            <ul style="padding-left: 20px; color: #d32f2f;">
                                                <li v-for="file in documentsStatus.missingGeneralFiles" :key="file.afId"
                                                    style="margin-bottom: 8px;">
                                                    {{ file.name }}
                                                    <span v-if="file.isRequired"
                                                        style="color: #f44336; font-weight: bold;"> *</span>
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- 顯示已上傳的通用文件列表 -->
                                        <div v-if="generalUploadedFiles && generalUploadedFiles.length > 0"
                                            style="margin-top: 15px;">
                                            <p style="font-weight: bold; margin-bottom: 10px;">Uploaded Files:</p>
                                            <ul style="padding-left: 20px; list-style: none;">
                                                <li v-for="file in generalUploadedFiles" :key="file.afId"
                                                    style="margin-bottom: 12px; color: #4caf50;">
                                                    <div style="margin-bottom: 5px;">✓ {{ file.name }}</div>
                                                    <a v-if="file.uploadedFile"
                                                        :href="$store.state.src + $store.state.subDirectory + file.uploadedFile.url"
                                                        target="_blank" class="file_a">
                                                        Uploaded File ({{ formatUploadTime(file.uploadedFile.createTime)
                                                        }})
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <!-- Department-Specific Documents (Step 5) -->
                                    <div>
                                        <h4
                                            style="margin-bottom: 15px; color: #580d63; border-bottom: 2px solid #580d63; padding-bottom: 5px;">
                                            Department-Specific Documents (Step 5)</h4>

                                        <div v-if="documentsStatus.departmentComplete"
                                            style="color: #4caf50; font-weight: bold; margin-bottom: 15px;">
                                            ✓ All required department-specific documents uploaded
                                        </div>
                                        <div v-else style="color: #f44336; font-weight: bold; margin-bottom: 15px;">
                                            ✗ Missing required documents
                                            <!-- <button class="btn" @click="$router.push('/Apply/5')"
                                                style="font-size: 14px; padding: 5px 15px; margin-left: 10px;">Go to
                                                Step 5</button> -->
                                        </div>

                                        <!-- 顯示缺少的系所文件列表 -->
                                        <div
                                            v-if="!documentsStatus.departmentComplete && documentsStatus.missingDepartmentFiles && documentsStatus.missingDepartmentFiles.length > 0">
                                            <p style="font-weight: bold; margin-top: 15px; margin-bottom: 10px;">Missing
                                                Files:</p>
                                            <ul style="padding-left: 20px; color: #d32f2f;">
                                                <li v-for="file in documentsStatus.missingDepartmentFiles"
                                                    :key="file.afId" style="margin-bottom: 8px;">
                                                    {{ file.name }} ({{ file.programName }})
                                                    <span v-if="file.isRequired"
                                                        style="color: #f44336; font-weight: bold;"> *</span>
                                                </li>
                                            </ul>
                                        </div>

                                        <!-- 顯示已上傳的系所文件列表 -->
                                        <div v-if="departmentUploadedFiles && departmentUploadedFiles.length > 0"
                                            style="margin-top: 15px;">
                                            <p style="font-weight: bold; margin-bottom: 10px;">Uploaded Files:</p>
                                            <ul style="padding-left: 20px; list-style: none;">
                                                <li v-for="file in departmentUploadedFiles"
                                                    :key="file.afId + '-' + file.pId"
                                                    style="margin-bottom: 12px; color: #4caf50;">
                                                    <div style="margin-bottom: 5px;">✓ {{ file.name }} ({{
                                                        file.programName }})</div>
                                                    <a v-if="file.uploadedFile"
                                                        :href="$store.state.src + $store.state.subDirectory + file.uploadedFile.url"
                                                        target="_blank" class="file_a">
                                                        Uploaded File ({{ formatUploadTime(file.uploadedFile.createTime)
                                                        }})
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 已提交狀態顯示 -->
                        <div v-if="isSubmitted" class="bg"
                            style="background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 20px;">
                            <h3 style="color: #2e7d32; margin-bottom: 15px; font-size: 20px;">
                                ✅ Application Successfully Submitted
                            </h3>
                            <p style="margin: 0; color: #1b5e20; font-size: 16px; line-height: 1.6;">
                                Your application has been submitted and is currently under review by the admissions
                                office.
                                You will be notified via email once the review process is complete.
                            </p>
                        </div>

                        <!-- 未提交時顯示確認框和提交按鈕 -->
                        <template v-else>
                            <!-- Confirmation Checkbox -->
                            <div class="bg">
                                <div class="form-group full check_block">
                                    <div class="form-check">
                                        <input class="form-check-input" type="checkbox" id="confirmAccurate"
                                            v-model="confirmAccurate" />
                                        <label class="form-check-label" for="confirmAccurate"
                                            style="font-weight: bold; color: #580d63;">
                                            <span class="required">*</span>
                                            I confirm that all the information above is correct
                                        </label>
                                    </div>
                                    <p class="notice" style="margin-top: 10px; color: #666;">
                                        By checking this box, you confirm that all information provided in your
                                        application
                                        is accurate and
                                        complete.
                                        False or misleading information may result in the rejection of your application
                                        or
                                        cancellation of your
                                        admission.
                                    </p>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <div class="btn_box">
                                <button class="btn" @click="gotoPrev">Previous</button>
                                <button class="btn" @click="submitApplication"
                                    :disabled="!confirmAccurate || !canSubmit || isSubmitting"
                                    :style="{ opacity: (!confirmAccurate || !canSubmit || isSubmitting) ? 0.5 : 1, cursor: (!confirmAccurate || !canSubmit || isSubmitting) ? 'not-allowed' : 'pointer' }">
                                    {{ isSubmitting ? 'Submitting...' : 'Submit Application' }}
                                </button>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>

        <Footer />
    </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import applyStudentService from "@/services/applyStudent.js";
import Swal from "sweetalert2";

export default {
    name: "Form_Check",
    components: {
        Header,
        Footer,
    },
    data() {
        return {
            // 進度步驟
            currentStep: 5, // 表示第7步（index 6）
            progressItems: [
                { to: "/Apply/1", title: "Fill out online application form" },
                { to: "/Apply/2", title: "Apply for a program" },
                {
                    to: "/Apply/3",
                    title:
                        "Fill out Recommenders (Do Not Fill Out Without Notifying Your Recommenders in Advance.)",
                },
                { to: "/Apply/4", title: "Upload Required Documents 1" },
                { to: "/Apply/5", title: "Upload Required Documents 2" },
                { to: "/Apply/Check", title: "Review & Submit" },
                { to: "/Apply/Pay", title: "Pay Application Fee" },
            ],

            // 載入狀態
            isLoading: false,
            isSubmitting: false,

            // 申請表ID
            asId: null,

            // 申請資料
            applicationData: null,

            // 使用者申請的學程列表
            userPrograms: [],

            // 推薦人列表
            recommenders: [],

            // 文件上傳狀態
            documentsStatus: null,
            generalUploadedFiles: [],
            departmentUploadedFiles: [],

            // 訂單資訊
            orderInfo: null,

            // 確認勾選
            confirmAccurate: false,
            isSubmitted: false, // 申請是否已提交

            // 選項列表 (用於顯示名稱)
            availableFinances: [],
            availableMessageSources: [],
            availableCountries: [],

            // 獎學金列表
            scholarships: [], // 國籍獎學金
            programScholarships: [], // 學程獎學金

            aId: null, // 申請學期梯次ID
        };
    },

    computed: {
        /**
         * 是否可以提交
         * 需要: 1) 所有必要文件已上傳 2) 已確認資料正確
         */
        canSubmit() {
            if (!this.documentsStatus) return false;

            // 檢查所有必要文件是否已上傳
            const documentsComplete = this.documentsStatus.allComplete;

            return documentsComplete && this.confirmAccurate;
        },
    },

    mounted() {
        window.scrollTo(0, 0);
        this.initializeForm();
    },

    methods: {
        // ========== 頁面初始化 ==========

        /**
         * 初始化表單 - 載入所有必要資料
         */
        async initializeForm() {
            this.isLoading = true;
            try {
                // 檢查使用者是否完成資格審查
                const checkResult = await applyStudentService.checkUserApplication();
                if (!checkResult.success || !checkResult.data ||
                    !checkResult.data?.isReview ||
                    checkResult.data?.isAgree != 1) {
                    Swal.fire({
                        icon: "warning",
                        title: "Eligibility review not completed",
                        text: "Please complete the dual citizenship check and agree to the terms and conditions first.",
                        confirmButtonText: "OK"
                    }).then(() => {
                        this.$router.push("/Profile");
                    });
                    return;
                }

                // 載入使用者申請表資料
                await this.loadUserApplication();

                // 若沒有申請表資料，跳轉回步驟1
                if (!this.applicationData || !this.asId) {
                    Swal.fire({
                        icon: "warning",
                        title: "No Application Data",
                        text: "Please complete Step 1 first.",
                        confirmButtonText: "OK"
                    }).then(() => {
                        this.$router.push("/Apply/1");
                    });
                    return;
                }

                // 載入申請的學程
                if (this.applicationData.applyPrograms) {
                    try {
                        const programIds = JSON.parse(this.applicationData.applyPrograms);
                        await this.loadProgramsInfo(programIds);
                    } catch (error) {
                        console.error("Error parsing applyPrograms:", error);
                    }
                }

                // 檢查是否選擇了學程
                if (this.userPrograms.length === 0) {
                    Swal.fire({
                        icon: "warning",
                        title: "No Programs Selected",
                        text: "Please select at least one program in Step 2.",
                        confirmButtonText: "OK"
                    }).then(() => {
                        this.$router.push("/Apply/2");
                    });
                    return;
                }

                // 載入推薦人資料
                await this.loadRecommenders();

                // 載入國籍獎學金資料
                await this.loadScholarships();

                // 載入學程獎學金資料
                await this.loadProgramScholarships();

                // 載入選項列表 (用於顯示名稱)
                await this.loadFinanceOptions();
                await this.loadMessageSourceOptions();
                await this.loadCountries();

                // 檢查必要文件上傳狀態
                await this.checkDocuments();

                // 如果有缺少的必要文件
                if (!this.documentsStatus.allComplete) {
                    Swal.fire({
                        icon: "warning",
                        title: "Missing Required Documents",
                        text: "You have missing required documents. Please complete all document uploads before submitting.",
                        confirmButtonText: "OK"
                    }).then(() => {
                        this.$router.push("/Apply/4");
                    });;
                }

            } catch (error) {
                console.error("Form initialization error:", error);
                Swal.fire({
                    icon: "error",
                    title: "Initialization Error",
                    text: "Failed to initialize form. Please refresh the page.",
                    confirmButtonText: "OK"
                });
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * 載入使用者申請表資料
         */
        async loadUserApplication() {
            const result = await applyStudentService.getUserApplication();

            if (result.success && result.data && result.data.hasApplication) {
                this.applicationData = result.data;
                this.aId = result.data.aId;
                this.asId = result.data.asId;
                this.isSubmitted = result.data.isSubmitted || false;
            } else {
                this.applicationData = null;
                this.aId = null;
                this.asId = null;
            }
        },

        /**
         * 載入學程資訊
         */
        async loadProgramsInfo(programIds) {
            const programsResult = await applyStudentService.getActiveApplyPrograms(this.aId);
            if (programsResult.success && programsResult.data) {
                const { programs, colleges } = programsResult.data;
                const allPrograms = programs || [];

                this.userPrograms = [];
                for (const pId of programIds) {
                    const program = allPrograms.find(p => p.pId === pId);
                    if (program) {
                        const college = colleges?.find(c => c.cId === program.cId);

                        this.userPrograms.push({
                            pId: pId,
                            college: college?.name_EN || college?.name || 'Unknown College',
                            program: program.name_EN || program.name,
                            degree: this.getDegreeText(program.degree),
                        });
                    }
                }
            }
        },

        /**
         * 載入國籍獎學金資料
         */
        async loadScholarships() {
            if (!this.applicationData || !this.applicationData.permCountry) return;

            const result = await applyStudentService.getAvailableScholarshipsByCountry(this.applicationData.permCountry);
            if (result.success && result.data) {
                // 篩選出已申請的獎學金 (有 applyStudentScholarship 且 isApply 為 true)
                this.scholarships = result.data
                    .filter(s => s.applyStudentScholarship && s.applyStudentScholarship.isApply)
                    .map(s => ({
                        sId: s.sId,
                        name: s.name || 'Scholarship',
                        content: s.content || '',
                        fileUrl: s.applyStudentScholarship?.url || '',
                        fileName: s.applyStudentScholarship?.fileName || '',
                        uploadTime: s.applyStudentScholarship?.uploadTime || '',
                        type: 'country' // 標記為國籍獎學金
                    }));

                console.log('Loaded country scholarships:', this.scholarships);
            } else {
                this.scholarships = [];
            }
        },

        /**
         * 載入學程獎學金資料
         */
        async loadProgramScholarships() {
            if (!this.applicationData || !this.applicationData.permCountry || this.userPrograms.length === 0) return;

            // 取得所有學程 ID
            const programIds = this.userPrograms.map(p => p.pId);

            const result = await applyStudentService.getAvailableScholarshipsByProgram(
                this.applicationData.permCountry,
                programIds
            );

            if (result.success && result.data) {
                // 篩選出已申請的學程獎學金 (有 applyStudentScholarship 且 isApply 為 true)
                this.programScholarships = result.data
                    .filter(s => s.applyStudentScholarship && s.applyStudentScholarship.isApply)
                    .map(s => ({
                        sId: s.sId,
                        name: s.name || 'Scholarship',
                        content: s.content || '',
                        fileUrl: s.applyStudentScholarship?.url || '',
                        fileName: s.applyStudentScholarship?.fileName || '',
                        uploadTime: s.applyStudentScholarship?.uploadTime || '',
                        type: 'program' // 標記為學程獎學金
                    }));

                console.log('Loaded program scholarships:', this.programScholarships);
            } else {
                this.programScholarships = [];
            }
        },

        /**
         * 載入推薦人資料
         */
        async loadRecommenders() {
            if (!this.asId) return;

            const result = await applyStudentService.getRecommenders(this.asId);
            if (result.success && result.data) {
                this.recommenders = result.data;
            }
        },    /**
     * 載入財務來源選項
     */
        async loadFinanceOptions() {
            const result = await applyStudentService.getAvailableFinances();
            if (result.success && result.data) {
                this.availableFinances = result.data;
            }
        },

        /**
         * 載入訊息來源選項
         */
        async loadMessageSourceOptions() {
            const result = await applyStudentService.getAvailableMessageSources();
            if (result.success && result.data) {
                this.availableMessageSources = result.data;
            }
        },

        /**
         * 載入國家列表
         */
        async loadCountries() {
            const result = await applyStudentService.getAvailableCountries();
            if (result.success && result.data) {
                this.availableCountries = result.data;
            }
        },

        /**
         * 檢查必要文件是否已上傳
         */
        async checkDocuments() {
            if (!this.asId) return;

            const result = await applyStudentService.checkRequiredDocuments(this.asId);
            if (result.success && result.data) {
                this.documentsStatus = result.data;
            }

            // 載入已上傳的通用文件
            await this.loadGeneralUploadedFiles();

            // 載入已上傳的系所文件
            await this.loadDepartmentUploadedFiles();
        },

        /**
         * 載入已上傳的通用文件
         */
        async loadGeneralUploadedFiles() {
            if (!this.asId) return;

            const result = await applyStudentService.getGeneralUploadFiles(this.asId);
            if (result.success && result.data) {
                // 篩選出已上傳的文件
                this.generalUploadedFiles = result.data.filter(file => file.uploadedFile);
            }
        },

        /**
         * 載入已上傳的系所文件
         */
        async loadDepartmentUploadedFiles() {
            if (!this.asId || this.userPrograms.length === 0) return;

            this.departmentUploadedFiles = [];

            for (const program of this.userPrograms) {
                const result = await applyStudentService.getDepartmentUploadFiles({
                    asId: this.asId,
                    pId: program.pId
                });

                if (result.success && result.data) {
                    // 篩選出已上傳的文件,並加上學程名稱
                    const uploadedFiles = result.data
                        .filter(file => file.uploadedFile)
                        .map(file => ({
                            ...file,
                            programName: program.program
                        }));

                    this.departmentUploadedFiles.push(...uploadedFiles);
                }
            }
        },

        // ========== 提交申請 ==========

        /**
         * 提交整個申請表
         */
        async submitApplication() {
            // 最終確認
            const result = await Swal.fire({
                title: "Submit Application?",
                html: `
          <p style="margin-bottom: 15px;">Are you sure you want to submit your application?</p>
          <p style="color: #f44336; font-weight: bold;">After submission:</p>
          <ul style="text-align: left; padding-left: 20px; color: #666;">
            <li>You will NOT be able to modify Steps 1-5</li>
            <li>You can only upload payment proof in the payment section</li>
            <li>Your application will be reviewed by the admissions office</li>
          </ul>
        `,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, submit",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#580d63",
                cancelButtonColor: "#999"
            });

            if (!result.isConfirmed) {
                return;
            }

            this.isSubmitting = true;

            try {
                const submitResult = await applyStudentService.submitUserApplication();

                if (submitResult.success) {
                    // 先詢問是否要列印申請表
                    Swal.fire({
                        icon: "success",
                        title: "Application Submitted!",
                        html: `
              <p>Your application has been successfully submitted.</p>
              <p style="margin-top: 15px; color: #666;">
                You will receive a confirmation email shortly.
              </p>
              <p style="margin-top: 15px; color: #f44336; font-weight: bold;">
                Please print the application form, sign it, and upload the signed copy in the payment section.
              </p>
            `,
                        confirmButtonText: "Print Now",
                        cancelButtonText: "Print Later",
                        showCancelButton: true,
                        confirmButtonColor: "#4caf50",
                        cancelButtonColor: "#999"
                    }).then((result) => {
                        if (result.isConfirmed) {
                            // 使用者選擇立即列印
                            this.printApplication();
                            // 列印後重新整理頁面
                            setTimeout(() => {
                                window.location.reload();
                            }, 500);
                        } else {
                            // 使用者選擇稍後列印，直接重新整理
                            window.location.reload();
                        }
                    });
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Submission Failed",
                        text: submitResult.message || "Failed to submit application. Please try again.",
                        confirmButtonText: "OK"
                    });
                }
            } catch (error) {
                console.error("Submit application error:", error);
                Swal.fire({
                    icon: "error",
                    title: "Submission Error",
                    text: "An error occurred while submitting. Please try again.",
                    confirmButtonText: "OK"
                });
            } finally {
                this.isSubmitting = false;
            }
        },

        // ========== UI 互動 ==========

        // 進度步驟 不同狀態增加不同class
        getStepClass(index) {
            if (index < this.currentStep) {
                return "done";
            } else if (index === this.currentStep) {
                return "filling";
            }
            return "";
        },

        // ========== 輔助方法 ==========

        /**
         * 取得性別的顯示文字
         */
        getGenderText(gender) {
            const genderMap = {
                0: "Unselected",
                1: "Male",
                2: "Female",
            };
            return genderMap[gender] || "N/A";
        },

        /**
         * 取得婚姻狀態的顯示文字
         */
        getMaritalStatusText(status) {
            const statusMap = {
                0: "Unselected",
                1: "Single",
                2: "Married",
            };
            return statusMap[status] || "N/A";
        },

        /**
         * 取得是/否的顯示文字
         */
        getYesNoText(value) {
            if (value === 1 || value === true) return "Yes";
            if (value === 2 || value === false) return "No";
            return "N/A";
        },

        /**
         * 取得語言能力的顯示文字
         */
        getLanguageProficiencyText(level) {
            const levelMap = {
                1: "Native",
                2: "Fluent",
                3: "Intermediate",
                4: "Basic",
                5: "None"
            };
            return levelMap[level] || "N/A";
        },

        /**
         * 格式化電話號碼
         */
        formatPhone(country, area, number) {
            if (!country && !area && !number) return "N/A";
            const parts = [];
            if (country) parts.push(`+${country}`);
            if (area) parts.push(area);
            if (number) parts.push(number);
            return parts.join(' ') || "N/A";
        },

        /**
         * 取得學位的顯示文字
         */
        getDegreeText(degree) {
            const degreeMap = {
                8: "Bachelor",
                10: "Master",
                12: "Doctoral"
            };
            return degreeMap[degree] || "";
        },

        /**
         * 取得國家名稱
         */
        getCountryName(countryCode) {
            if (!countryCode) return "N/A";
            
            const country = this.availableCountries.find(c => c.code === countryCode);
            return country ? (country.name_EN || country.name || countryCode) : countryCode;
        },

        /**
         * 取得財務來源文字
         */
        getFinanceSourcesText() {
            if (!this.applicationData.financial) return "N/A";

            try {
                const sources = JSON.parse(this.applicationData.financial);
                if (!sources || sources.length === 0) return "N/A";

                return sources.map(source => {
                    // 根據 fId 查找名稱
                    const financeItem = this.availableFinances.find(f => f.fId === source.fId);
                    let text = financeItem ? (financeItem.name_EN || financeItem.name) : `Finance ID: ${source.fId}`;

                    if (source.scholarshipText) {
                        text += ` (${source.scholarshipText})`;
                    }
                    if (source.otherText) {
                        text += ` (${source.otherText})`;
                    }
                    return text;
                }).join(', ');
            } catch (error) {
                console.error("Error parsing finance sources:", error);
                return this.applicationData.financial;
            }
        },    /**
     * 取得訊息來源文字
     */
        getMessageSourcesText() {
            if (!this.applicationData.messageSource) return "N/A";

            try {
                const sources = JSON.parse(this.applicationData.messageSource);
                if (!sources || sources.length === 0) return "N/A";

                return sources.map(source => {
                    // 根據 msId 查找名稱
                    const sourceItem = this.availableMessageSources.find(ms => ms.msId === source.msId);
                    let text = sourceItem ? (sourceItem.name_EN || sourceItem.name) : `Message Source ID: ${source.msId}`;

                    if (source.otherText) {
                        text += ` (${source.otherText})`;
                    }
                    return text;
                }).join(', ');
            } catch (error) {
                console.error("Error parsing message sources:", error);
                return this.applicationData.messageSource;
            }
        },

        /**
         * 檢查是否有獎學金申請
         */
        hasScholarshipApplications() {
            return (this.scholarships && this.scholarships.length > 0) ||
                (this.programScholarships && this.programScholarships.length > 0);
        },

        /**
         * 取得已申請的獎學金列表 (合併國籍與學程獎學金)
         */
        getAppliedScholarships() {
            const countryScholarships = this.scholarships || [];
            const programScholarships = this.programScholarships || [];
            return [...countryScholarships, ...programScholarships];
        },

        /**
         * 取得 ITRI 學位文字
         */
        getITRIDegreeText(degree) {
            const degreeMap = {
                1: "Bachelor",
                2: "Master",
                3: "Ph.D.",
                4: "Other"
            };
            return degreeMap[degree] || "N/A";
        },

        /**
         * 取得 ITRI 研究興趣文字
         */
        getITRIResearchInterests() {
            const interests = [];

            if (this.applicationData.interestSystemsSoftware) interests.push("Systems Software");
            if (this.applicationData.interest5GCommunications) interests.push("5G Communications");
            if (this.applicationData.interestMachineLearning) interests.push("Machine Learning");
            if (this.applicationData.interestVRAR) interests.push("Virtual, Augmented Reality");
            if (this.applicationData.interestDeepNeuralNetwork) interests.push("Deep Neural Network");
            if (this.applicationData.interestDrone) interests.push("Drone");
            if (this.applicationData.interestCyberSecurity) interests.push("Cyber Security");
            if (this.applicationData.interestRobotics) interests.push("Robotics, Precision Manufacturing");
            if (this.applicationData.interestAutonomousVehicles) interests.push("Autonomous Vehicles, Electric Vehicles");
            if (this.applicationData.interest3DIC) interests.push("3DIC, LED, OLED, Optical Systems");
            if (this.applicationData.interest3DPrinting) interests.push("3D Printing, CNC, Machine Tools");

            return interests.length > 0 ? interests.join(', ') : 'N/A';
        },

        /**
         * 檢查是否有高中資訊
         */
        hasHighSchoolInfo() {
            return this.applicationData.highSchoolName ||
                this.applicationData.highSchoolCity ||
                this.applicationData.highSchoolCountry;
        },

        /**
         * 檢查是否有大學資訊
         */
        hasUndergraduateInfo() {
            return this.applicationData.undergraduateName ||
                this.applicationData.undergraduateCity ||
                this.applicationData.undergraduateCountry;
        },

        /**
         * 檢查是否有研究所資訊
         */
        hasGraduateInfo() {
            return this.applicationData.graduateName ||
                this.applicationData.graduateCity ||
                this.applicationData.graduateCountry;
        },

        /**
         * 格式化上傳時間
         */
        formatUploadTime(timestamp) {
            if (!timestamp) return 'N/A';

            try {
                const date = new Date(timestamp);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${year}-${month}-${day} ${hours}:${minutes}`;
            } catch (error) {
                console.error('Error formatting upload time:', error);
                return timestamp;
            }
        },

        // ========== 頁面導覽 ==========

        gotoPrev() {
            this.$router.push("/Apply/Pay").then(() => {
                window.scrollTo(0, 0);
            });
        },

        /**
         * 列印申請表 - 在新視窗中開啟列印頁面
         */
        printApplication() {
            if (!this.applicationData) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No application data available for printing.",
                    confirmButtonText: "OK"
                });
                return;
            }
            
            // 在新視窗中開啟列印頁面
            const routeData = this.$router.resolve({ name: 'ApplicationFormPrintPage' });
            window.open(routeData.href, '_blank');
        },
    },
};
</script>

<style scoped>
</style>
